import React, { useState } from 'react';
import { PisoEstado } from '../services/dashboardService';
import { ESTADO_INFO, ORDEN_ESTADO, detalleEstado, estadoVisible, EstadoVisible, nombreSinPiso } from '../utils/estadoArea';
import Chevron from './Chevron';

interface PisoStatusOverviewProps {
  pisosStatus: PisoEstado[];
  isLoading?: boolean;
}

const CONTADORES: EstadoVisible[] = ['rojo', 'amarillo', 'verde', 'pausada'];

// Una fila por piso con su barra de estados: todos los pisos caben en una
// pantalla. Al tocar un piso se abren sus áreas en columnas parejas.
const PisoStatusOverview: React.FC<PisoStatusOverviewProps> = ({ pisosStatus, isLoading = false }) => {
  const [abiertos, setAbiertos] = useState<Set<number>>(() => new Set());

  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-4 sm:p-6 animate-pulse space-y-3">
        <div className="h-6 bg-gray-200 rounded w-1/3" />
        {[1, 2, 3].map((i) => <div key={i} className="h-10 bg-gray-100 rounded" />)}
      </div>
    );
  }

  const pisos = (pisosStatus || [])
    .map((piso) => ({
      ...piso,
      areas: piso.areas
        .filter((a) => a.activo !== false)
        .sort((a, b) => ORDEN_ESTADO[estadoVisible(a)] - ORDEN_ESTADO[estadoVisible(b)] || a.nombre.localeCompare(b.nombre)),
    }))
    .filter((piso) => piso.areas.length > 0);

  const todosAbiertos = pisos.length > 0 && pisos.every((p) => abiertos.has(p.pisoId));

  const alternar = (pisoId: number) =>
    setAbiertos((prev) => {
      const next = new Set(prev);
      if (next.has(pisoId)) next.delete(pisoId);
      else next.add(pisoId);
      return next;
    });

  return (
    <div className="bg-white shadow rounded-lg p-4 sm:p-6">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Estado por piso</h2>
        {pisos.length > 0 && (
          <button
            type="button"
            onClick={() => setAbiertos(todosAbiertos ? new Set() : new Set(pisos.map((p) => p.pisoId)))}
            className="text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded"
          >
            {todosAbiertos ? 'Cerrar todos' : 'Abrir todos'}
          </button>
        )}
      </div>
      <p className="text-sm text-gray-600 mb-3">Toca un piso para ver sus áreas.</p>

      {pisos.length === 0 ? (
        <div className="text-center text-gray-500 py-6">No hay áreas activas configuradas.</div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {pisos.map((piso) => {
            const abierto = abiertos.has(piso.pisoId);
            const conteo = CONTADORES.map((estado) => ({
              estado,
              cantidad: piso.areas.filter((a) => estadoVisible(a) === estado).length,
            })).filter((c) => c.cantidad > 0);

            return (
              <li key={piso.pisoId}>
                <button
                  type="button"
                  onClick={() => alternar(piso.pisoId)}
                  aria-expanded={abierto}
                  className="w-full flex flex-wrap sm:flex-nowrap items-center gap-x-4 gap-y-2 px-2 py-3 text-left rounded hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                >
                  <span className="order-1 sm:w-24 flex-shrink-0 font-semibold text-gray-900">{piso.pisoNombre}</span>

                  {/* Barra proporcional: cuánto del piso está en cada estado */}
                  <span className="order-3 sm:order-2 basis-full sm:basis-auto sm:flex-1 flex h-2.5 rounded-full overflow-hidden bg-gray-100" aria-hidden="true">
                    {conteo.map(({ estado, cantidad }) => (
                      <span
                        key={estado}
                        className={ESTADO_INFO[estado].punto}
                        style={{ width: `${(cantidad / piso.areas.length) * 100}%` }}
                      />
                    ))}
                  </span>

                  {/* Ancho fijo en pantallas anchas para que todas las barras terminen a la misma altura */}
                  <span className="order-2 sm:order-3 ml-auto sm:w-72 flex-shrink-0 flex items-center justify-end gap-3">
                    <span className="flex flex-wrap justify-end gap-x-3 gap-y-1 text-sm">
                      {conteo.map(({ estado, cantidad }) => (
                        <span key={estado} className="inline-flex items-center gap-1.5 text-gray-700 whitespace-nowrap">
                          <span className={`w-2 h-2 rounded-full ${ESTADO_INFO[estado].punto}`} aria-hidden="true" />
                          {cantidad} {ESTADO_INFO[estado].etiqueta.toLowerCase()}
                        </span>
                      ))}
                    </span>
                    <Chevron abierto={abierto} />
                  </span>
                </button>

                {abierto && (
                  <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 px-2 pb-3">
                    {piso.areas.map((area) => {
                      const estado = estadoVisible(area);
                      return (
                        <li
                          key={area.id}
                          className="flex items-center gap-2 py-1.5 text-sm min-w-0"
                          title={estado === 'pausada' ? area.nombre : `${area.nombre} · ${detalleEstado(area)}`}
                        >
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${ESTADO_INFO[estado].punto}`} aria-hidden="true" />
                          <span className="truncate text-gray-900">{nombreSinPiso(area.nombre, piso.pisoNombre)}</span>
                          <span className={`ml-auto pl-2 text-xs whitespace-nowrap ${ESTADO_INFO[estado].texto}`}>
                            {ESTADO_INFO[estado].etiqueta}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default PisoStatusOverview;
